import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle, Lock, Star, Award, Clock, BookMarked, ArrowRight, ShieldCheck, Circle, Sparkles } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponValid, setCouponValid] = useState(null);
  const [online, setOnline] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/api/courses/${slug}`).then(({ data }) => setCourse(data))
      .catch(() => toast.error("Course not found"))
      .finally(() => setLoading(false));
    api.get(`/api/courses/${slug}/preview`).then(({ data }) => setPreview(data)).catch(() => {});
    api.get(`/api/courses/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.get(`/api/widgets/online-users?scope=course-${slug}`).then(({ data }) => setOnline(data.count)).catch(() => {});
  };

  useEffect(() => { load(); }, [slug]);

  const buy = async () => {
    if (!user) { nav("/login", { state: { from: `/courses/${slug}` } }); return; }
    if (user.role === "admin") { toast.info("Admins already have access"); return; }
    setPaying(true);
    try {
      const payload = { course_id: course.id, origin_url: window.location.origin };
      if (coupon && couponValid) payload.coupon_code = coupon;
      const { data } = await api.post("/api/payments/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/api/coupons/validate", { code: coupon, course_id: course.id });
      setCouponValid(data);
      toast.success(`${data.percent_off}% off applied`);
    } catch (e) {
      setCouponValid(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  if (loading) return <div className="py-24 text-center">Loading...</div>;
  if (!course) return <div className="py-24 text-center">Course not found.</div>;

  const previewLessons = course.modules
    .flatMap((m) => m.lessons.map((l) => ({ ...l, moduleTitle: m.title, moduleId: m.id })))
    .filter((l) => l.is_preview);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="course-detail">
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="label-eyebrow">{course.category_name} · {course.level} · {course.language}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tight">{course.title}</h1>
          <p className="text-lg text-[#4B5563] mt-4 leading-relaxed">{course.short_description}</p>

          {preview?.partner && (
            <div className="mt-4 inline-flex items-center gap-2 bg-[#F0FDF4] border border-[#BBF7D0] text-[#065F46] px-3 py-1.5 rounded-full text-xs font-semibold"
              data-testid="course-partner-badge">
              <ShieldCheck className="w-3.5 h-3.5" />
              Delivered by {preview.partner.organization_name}, in partnership with Teacher4Me
            </div>
          )}

          {(preview?.screenshots?.length > 0 || preview?.teaser_video || preview?.demo_video || preview?.free_lesson) && (
            <section className="mt-8" data-testid="course-preview-section">
              <h2 className="font-display font-bold text-2xl mb-3">Course preview</h2>

              {preview.teaser_video && (
                <div className="mb-4" data-testid="course-preview-teaser">
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-2">60-second teaser</div>
                  <video src={preview.teaser_video} controls
                    className="w-full max-h-72 rounded-lg bg-black" />
                </div>
              )}

              {preview.demo_video && (
                <div className="mb-4" data-testid="course-preview-demo">
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-2">Walkthrough</div>
                  <video src={preview.demo_video} controls
                    className="w-full max-h-96 rounded-lg bg-black" />
                </div>
              )}

              {preview.screenshots?.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs uppercase text-gray-500 tracking-wider mb-2">Screenshots</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="course-preview-shots">
                    {preview.screenshots.map((url, i) => (
                      <img key={`shot-${i}`} src={url} alt={`Preview ${i+1}`}
                        className="w-full h-32 object-cover rounded-lg shadow-sm border" />
                    ))}
                  </div>
                </div>
              )}

              {preview.free_lesson && (
                <div className="mt-4 p-4 bg-[#F0FDFA] border border-[#99F6E4] rounded-lg"
                  data-testid="course-preview-free-lesson">
                  <div className="text-xs uppercase text-[#0F766E] tracking-wider mb-1">Free lesson preview</div>
                  <div className="font-semibold mb-2">{preview.free_lesson.title}</div>
                  <div className="text-sm text-gray-700 whitespace-pre-line max-h-40 overflow-y-auto">
                    {preview.free_lesson.body}
                  </div>
                </div>
              )}
            </section>
          )}

          <div className="flex flex-wrap gap-5 mt-6 text-sm text-[#4B5563]">
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#D97706] text-[#D97706]" /> {course.rating} ({course.review_count} reviews)</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {course.duration_hours} hours</span>
            <span className="flex items-center gap-1.5"><BookMarked className="w-4 h-4" /> {course.modules.length} modules</span>
            <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#064E3B]" /> {course.certificate_title}</span>
            {online != null && (
              <span data-testid="course-online" className="flex items-center gap-1.5 text-[#064E3B] bg-[#D1FAE5] px-2 py-0.5 rounded-full font-semibold text-xs">
                <Circle className="w-2 h-2 fill-[#10B981] text-[#10B981] animate-pulse" />
                Currently {online} online
              </span>
            )}
          </div>

          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl">What you&apos;ll learn</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {course.learning_outcomes.map((o, i) => (
                <div key={`outcome-${i}-${(o || "").slice(0, 24)}`} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#064E3B] mt-0.5 shrink-0" />
                  <span className="text-sm">{o}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl">About this course</h2>
            <p className="mt-3 text-[#4B5563] whitespace-pre-line leading-relaxed">{course.description}</p>
          </section>

          {course.target_audience && (
            <section className="mt-8">
              <h3 className="font-display font-bold text-lg">Who this course is for</h3>
              <p className="mt-2 text-[#4B5563]">{course.target_audience}</p>
            </section>
          )}

          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl">Course modules</h2>
            <div className="mt-4 border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]" data-testid="modules-list">
              {course.modules.map((m, i) => (
                <div key={m.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="label-eyebrow">Module {i+1}</div>
                      <div className="font-display font-bold text-lg">{m.title}</div>
                    </div>
                    <span className="text-xs text-[#4B5563]">{m.lessons.length} lessons</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {m.lessons.map((l) => (
                      <li key={l.id} className="text-sm flex items-center gap-2">
                        {l.is_preview ? (
                          <Link to={`/courses/${course.slug}/preview/${l.id}`} data-testid={`preview-${l.id}`} className="text-[#D92D20] font-semibold hover:underline flex items-center gap-1">
                            {l.title} <span className="text-[10px] bg-[#FECACA] text-[#7F1D1D] px-1.5 py-0.5 rounded uppercase">Free preview</span>
                          </Link>
                        ) : (
                          <span className="text-[#4B5563] flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {l.title}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {previewLessons.length > 0 && (
            <section className="mt-8 bg-[#064E3B]/5 border border-[#064E3B]/20 rounded-xl p-6">
              <div className="label-eyebrow text-[#064E3B]">Free preview</div>
              <h3 className="font-display font-bold text-xl mt-1">Try a lesson before you buy</h3>
              <div className="mt-4 flex flex-wrap gap-3">
                {previewLessons.map((l) => (
                  <Link key={l.id} to={`/courses/${course.slug}/preview/${l.id}`} data-testid={`preview-cta-${l.id}`}
                    className="btn-outline text-sm flex items-center gap-1">
                    {l.title} <ArrowRight className="w-4 h-4" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Reviews */}
          <section className="mt-12">
            <h2 className="font-display font-bold text-2xl">Student reviews</h2>
            {reviews.length === 0 && <div className="mt-3 text-sm text-[#4B5563]">No reviews yet. Enrolled students can leave a review.</div>}
            <div className="mt-4 space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="border border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold">{r.user_name}</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({length: 5}).map((_, i) => (
                        <Star key={`star-${r.id}-${i}`} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#D97706] text-[#D97706]" : "text-[#E5E7EB]"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm mt-2 text-[#4B5563]">{r.comment}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar buy card */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-sm">
            <img src={course.thumbnail_url} alt={course.title} className="aspect-[16/9] w-full object-cover" />
            <div className="p-6">
              <div className="flex items-end justify-between">
                <div className="font-display font-black text-4xl">${course.price_usd}</div>
                <div className="text-xs label-eyebrow text-[#064E3B] flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Secure Checkout</div>
              </div>
              <p className="text-xs text-[#4B5563] mt-1">One-time payment · Lifetime access</p>

              {!course.enrolled ? (
                <>
                  {/* Coupon input */}
                  <div className="mt-4">
                    <label className="label-eyebrow">Have a coupon?</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        data-testid="coupon-input"
                        value={coupon}
                        onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponValid(null); }}
                        placeholder="WELCOME20"
                        className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-[#D92D20]"
                      />
                      <button onClick={applyCoupon} data-testid="apply-coupon" className="btn-outline text-sm">Apply</button>
                    </div>
                    {couponValid && (
                      <div className="mt-2 text-xs text-[#064E3B] font-semibold" data-testid="coupon-applied">
                        ✓ {couponValid.percent_off}% off → New price: ${couponValid.discounted_amount?.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <button data-testid="buy-now" onClick={buy} disabled={paying} className="btn-primary w-full mt-4">
                    {paying ? "Redirecting..." : (couponValid ? `Buy Now — $${couponValid.discounted_amount?.toFixed(2)}` : "Buy Now")}
                  </button>
                </>
              ) : (
                <Link to={`/learn/${course.id}`} data-testid="continue-learning" className="btn-secondary w-full mt-5 inline-flex items-center justify-center">
                  Continue Learning
                </Link>
              )}

              {previewLessons[0] && (
                <Link to={`/courses/${course.slug}/preview/${previewLessons[0].id}`} className="btn-outline w-full mt-2 inline-flex items-center justify-center" data-testid="watch-preview">
                  Watch free preview
                </Link>
              )}

              <ul className="mt-6 space-y-2.5 text-sm">
                <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#4B5563]" /> {course.duration_hours} hours of content</li>
                <li className="flex items-center gap-2"><BookMarked className="w-4 h-4 text-[#4B5563]" /> {course.modules.length} modules · {course.modules.reduce((a, m) => a + m.lessons.length, 0)} lessons</li>
                <li className="flex items-center gap-2"><Award className="w-4 h-4 text-[#064E3B]" /> Certificate of completion</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#064E3B]" /> AI Tutor access for paid students</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
